import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { DIALOG_TYPE } from '../../../constants/DialogTypes';
import { Button } from '../../../ui/components/Common/Buttons/Button';
import { getOrganizationById } from '../../../utils/api_service/organization';
import { Organization } from './../types';
import BillingHistoryTab from './containers/BillingHistoryTab';
import BillingSubscriptionTab from './containers/BillingSubscriptionTab';
import OverviewTab from './containers/OverviewTab';
import PermissionsTab from './containers/PermissionsTab';
import SecurityLogTab from './containers/SecurityLogTab';
import UsersTab from './containers/UsersTab';

const OrganizationDetails = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('Overview');

  const [organization, setOrganization] = useState<Organization>({
    id: '',
    organizationName: '',
    primaryDomain: '',
    organizationDomains: [],
    companyLogo: '',
    companyDetails: '',
    perksAndBenefits: '',
    replyEmail: '',
    supportEmail: '',
    supportPhone: '',
    customerId: '',
  });

  const tabs = [
    'Overview',
    'Billing & Subscription',
    'Billing History',
    'Users',
    'Permissions',
    'Security Log',
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Overview':
        return <OverviewTab />;
      case 'Billing & Subscription':
        return <BillingSubscriptionTab />;
      case 'Billing History':
        return <BillingHistoryTab />;
      case 'Users':
        return <UsersTab />;
      case 'Permissions':
        return <PermissionsTab />;
      case 'Security Log':
        return <SecurityLogTab />;
      default:
        return <OverviewTab />;
    }
  };

  useEffect(() => {
    getOrganization();
  }, []);

  const getOrganization = async () => {
    try {
      const res = await getOrganizationById(id);
      if (res.ok) {
        const response = await res.json();
        setOrganization(response);
      }

    } catch (e) {
      console.error(e);
    }
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(word => word[0].toUpperCase())
      .join('');
  };

  const generateColor = (name: string): string => {
    const colors = [
      'bg-blue-500', 'bg-emerald-500', 'bg-violet-500',
      'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className='p-8 bg-slate-50/50 min-h-screen font-sans'>
      <div className='max-w-[1600px] mx-auto'>
        { /* Breadcrumbs */ }
        <nav className='flex items-center gap-2 mb-6 text-sm font-medium'>
          <span
              className='text-slate-400 cursor-pointer hover:text-slate-600 transition-colors'
              onClick={() => history.push('/organizations')}
          >
            Organizations
          </span>
          <svg
              className='w-3 h-3 text-slate-300'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={3}
                d='M9 5l7 7-7 7'
            />
          </svg>
          <span className='text-emerald-600'>{ organization.organizationName }</span>
        </nav>

        { /* Top Header Card */ }
        <div className='bg-white rounded-[32px] border border-slate-100 shadow-xl shadow-slate-200/40 p-8 mb-8'>
          <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6'>
            <div className='flex items-center gap-6'>
              <div
                  className='w-24 h-24 rounded-3xl flex items-center justify-center p-4 outline outline-2 outline-gray-300'>
                { organization.companyLogo ? (
                  <img
                      src={organization.companyLogo}
                      alt={organization.organizationName || 'Company Logo'}
                      className='rounded-full object-cover shadow-sm'
                  />
                ) : (
                  <div
                      className={`w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-xl ${generateColor(organization.organizationName ?? '')}`}
                  >
                    { getInitials(organization.organizationName ?? '') }
                  </div>
                ) }
              </div>
              <div>
                <div className='flex items-center gap-3 mb-1'>
                  <h1 className='text-xl font-black text-slate-800 tracking-tighter'>
                    { organization.organizationName }
                  </h1>
                  <span
                      className='px-3 py-1 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full text-[10px] font-black uppercase tracking-widest'>
                    Enterprise
                  </span>
                </div>
                <p className='text-slate-400 text-sm font-bold tracking-tight mb-2'>
                  Tenant ID:{ ' ' }
                  <span className='text-emerald-600'>ACME-908877-ORG</span>
                </p>
                <div className='flex items-center gap-4 text-xs font-bold text-slate-400'>
                  <div className='flex items-center gap-1.5'>
                    <svg
                        className='w-4 h-4'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                      />
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                    </svg>
                    San Francisco, CA
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <svg
                        className='w-4 h-4'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                      />
                    </svg>
                    Joined June 2021
                  </div>
                </div>
              </div>
            </div>
            <div className='flex flex-wrap gap-3'>
              <Button
                  label='Edit Profile'
                  variant='primary'
                  className='rounded-xl px-6 py-2.5 font-bold shadow-lg shadow-emerald-200/50'
              />
              <Button
                  label='Manage Licenses'
                  variant='secondary'
                  className='rounded-xl px-6 py-2.5 font-bold border-slate-200 hover:bg-slate-50'
              />
              <Button
                  label='Suspend'
                  variant='primary'
                  dialogType={DIALOG_TYPE.ERROR}
                  className='rounded-xl px-6 py-2.5 font-bold shadow-lg shadow-red-200/50'
              />
            </div>
          </div>

          { /* Tab Navigation */ }
          <div className='flex border-b border-slate-100 mt-10 overflow-x-auto scrollbar-hide'>
            { tabs.map((tab) => (
              <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-xs font-bold tracking-widest uppercase transition-all relative whitespace-nowrap ${
                    activeTab === tab
                      ? 'text-emerald-600'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
              >
                { tab }
                { activeTab === tab && (
                  <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-500 rounded-t-full' />
                ) }
              </button>
            )) }
          </div>
        </div>

        { /* Content Area */ }
        { renderTabContent() }
      </div>
    </div>
  );
};

export default OrganizationDetails;
